import { EntryType, Octopus, OpenAI } from '@maximizer/core/shared/domain';
import type {
  LangGraphConfirmation,
  LangGraphHandoff,
} from '../types/langgraph.types';
import { computed } from '@angular/core';
import { integrationTranslateConfiguration } from '@maximizer/core/shared/ui/config';

export enum ViewType {
  Report,
  DetailedReport,
  Chat,
  PrivacySecurityCenter,
}

export enum StreamingPhase {
  GettingContext,
  Anonymizing,
  ProcessingCustomDataSources,
  Thinking,
  WebSearch,
  GettingContextReadingRecords,
  GettingContextReadingDocuments,
  GettingContextFindingRelevant,
}

export enum IQChatFrom {
  User,
  Ai,
}

export type EnvironmentType = 'development' | 'qa' | 'staging' | 'production';
export type IQBoostPromptConfig = OpenAI.RequestConfiguration &
  LangfuseResponseExtension;

export const CODE_ADMIN = 'ADMIN';
export const IQBOOST_V2_FEATURE_FLAG = 'tmp-core-ai-langgraph';
export const DEFAULT_LANGUAGE = 'en';
export const CODE_3_FILTER_CONFIGURATION = 'aiIQBoostConfigurationFilters';
export const CODE_4_FILTER_CONFIGURATION = 'filterConfiguration';
export const CODE_3_AI_ENABLED = 'AI';
export const CODE_4_AI_ENABLED = 'Enabled';
export const CODE_3_AI_USER_DATA = 'AI_USER_DATA';
export const CODE_4_AI_USER_DATA = 'DATA';
export const CODE_4_HUB_USER_DATA = 'HUB_USER_DATA';

export const DefaultDataFiltering: DataFiltering = {
  dueDate: Octopus.DateOptionId.LASTTWELVEMONTHS,
} as DataFiltering;

export const DefaultDataScope = {
  all: true,
  householdDetails: true,
  accountDetails: true,
  notes: true,
  emails: true,
  documents: true,
  calls: true,
  allInteractions: true,
  dataFiltering: DefaultDataFiltering,
} as DataScope;

export const DefaultDataProcessingFeatures: IQDataProcessingFeatures = {
  anonymized: true,
  useGlobalServers: false,
  useWebData: false,
};

export function isGlobalServersInEffect(
  adminFeatures: Pick<IQDataProcessingFeatures, 'useGlobalServers'> | null | undefined,
  userGlobalServersEnabled: boolean,
  iqBoostV2: boolean,
): boolean {
  return iqBoostV2 || (adminFeatures?.useGlobalServers === true && userGlobalServersEnabled);
}

export interface IQBoostCreateCRMAction {
  key: string;
  for?: string;
  displayText: string;
  title: string;
  description: string;
  action: string | undefined;
  date: string;
  openDialog: boolean;
}

export interface LangfuseResponseExtension {
  maximizer: {
    source: Array<{ type: string; order?: number }>;
    title: string | LangfuseResponseExtensionTitle;
    priviliges?: {
      create: boolean;
      references: boolean;
    };
    systemType?: string;
    suggestedPrompts?: LangfuseResponseExtensionSuggestedPrompt[];
    smartSearch?: LangfuseResponseExtensionSmartSearch;
    hiddenFromDatabases?: Array<string>;
  } & LangfuseModelConfigOverride &
    LangfuseModelConfigDataSource;
  variables?: Record<string, LangfuseResponseExtensionVariable>;
}

export interface LangfuseResponseExtensionVariable {
  value?: string;
  translationValue?: LangfuseResponseExtensionTranslation;
}

export interface LangfuseResponseExtensionSmartSearch {
  min_weight: number;
}

export interface LangfuseResponseExtensionSuggestedPrompt {
  text: string;
  /** The language the text is written in, e.g. `en` or `fr`. */
  type?: unknown;
  /** Classes of its icon when shown as a starter card, e.g. `fa-icons icon-light icon-users`. */
  iconClass?: string;
}

export interface LangfuseResponseExtensionTitle {
  value: string | LangfuseResponseExtensionTranslation;
  category: {
    value: string | LangfuseResponseExtensionTranslation;
    iconStyle: string;
  };
}

export interface LangfuseResponseExtensionTranslation {
  [key: string]: string;
}

export interface LangfuseResponseExtensionSource {
  type: string;
  order?: number;
}

export interface LangfuseModelConfigOverride {
  globalModelConfigOverride?: {
    model: string;
    temperature: number;
    max_tokens: number;
  };
}

export interface LangfuseModelConfigDataSource {
  data_source?: {
    data_scope: LangfuseModelConfigDataScope;
  };
}

export interface LangfuseModelConfigDataScope {
  address_book: boolean;
  opportunity: boolean;
  notes: boolean;
  emails: boolean;
  documents: boolean;
  calls: boolean;
  all_interactions: boolean;
}

export interface DataScope {
  all: boolean;
  householdDetails: boolean;
  accountDetails: boolean;
  notes: boolean;
  emails: boolean;
  documents: boolean;
  calls: boolean;
  allInteractions: boolean;
  dataFiltering?: DataFiltering;
}

export interface DataScopeRequest {
  addressBook: boolean;
  opportunity: boolean;
}

export interface DataFiltering {
  dueDate: Octopus.DateOptionId;
}

export interface AskAiInput {
  key?: string;
  type?: EntryType;
  userPrompt?: string;
  conversationId?: string;
  invalidateCache?: boolean;
}

export interface IQBoostContextUserMessages {
  context: OpenAI.GPTMessage[];
  user: OpenAI.GPTMessage[];
}

export interface IQBoostConversationMessages extends IQBoostContextUserMessages {
  system: OpenAI.GPTMessage[];
}

export interface ScopeChangeRequest {
  useAllHouseholds: boolean;
}

/** Something the agent did on its way to an answer, e.g. a tool it ran; shown with the answer. */
export interface IQChatStep {
  /** The tool that ran, as the orchestrator names it (snake_case). */
  tool: string;
}

/** A file a question was sent with, by name only: the file itself stays with the thread. */
export interface IQChatAttachment {
  /** The file's name, extension included. */
  name: string;
}

/** The reader's thumbs up or down on an answer. */
export type IQChatFeedback = 'positive' | 'negative';

export interface IQChatAddon {
  override: string;
  ref: string;
}

export interface IQChat {
  content: string;
  from: IQChatFrom;
  /** On questions of the IQ Boost v2 chats only: the files sent with it, in the order attached. */
  attachments?: IQChatAttachment[];
  /** On answers of the IQ Boost v2 chats only: the steps that led to it, in order. */
  steps?: IQChatStep[];
  /** On answers of the IQ Boost v2 chats only: the reader's vote, once given. */
  feedback?: IQChatFeedback;
  addon?: IQChatAddon;
  /** On answers of the IQ Boost v2 chats only: what the orchestrator handed over with it. */
  handoffs?: LangGraphHandoff[];
  /** On answers of the IQ Boost v2 chats only: the changes the reader was asked to confirm. */
  confirmations?: LangGraphConfirmation[];
}

export interface IQChatGroup {
  chatHistory: IQChat[];
  id: string;
}

export interface IQAiResponse {
  summary: string;
}

export interface IQDataProcessingFeatures {
  anonymized: boolean;
  useGlobalServers: boolean;
  useWebData: boolean;
}

export interface ProcessingFeaturesByRow {
  admin: IQDataProcessingFeatures;
  user: IQDataProcessingFeatures;
}

export interface IQBoostSavedConfigurationUser {
  wasFirstTimeOpenedPrivacySecurityCenter: boolean;
  dataProcessingFeatures: IQDataProcessingFeatures;
  dataScope: DataScope;
  recentUserPrompts: string[];
}

export interface IQBoostSavedConfigurationAdmin {
  dataProcessingFeatures: IQDataProcessingFeatures;
}

export interface IQDataProcessingFeaturesGroup {
  admin: IQBoostSavedConfigurationAdmin;
  user: IQBoostSavedConfigurationUser;
  isAdmin: boolean;
}

export class LanguageHelper {
  static readonly language = computed(() => {
    return (
      top?.maximizerIdentity?.language ??
      integrationTranslateConfiguration.fallbackLang ??
      DEFAULT_LANGUAGE
    ).toLowerCase();
  });
}

export interface SmartSearchResponse extends Octopus.Response {
  Data?: {
    timeline: Octopus.Timeline[];
    addressBook: Octopus.Entity[];
    opportunity: Octopus.Entity[];
    documents: DocumentContentResponse[];
  };
}

export interface DocumentContentResponse {
  Key: string;
  FileName: string;
  Content: string;
}
